import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import { idbPersister } from '@/lib/queryPersister'
import { PERSISTED_CACHE_BUSTER } from '@/lib/persistedCacheRestore'

export const PERSISTED_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 7

let gatedPersistedClient: PersistedClient | undefined = undefined

export function setGatedPersistedClient(client: PersistedClient | undefined): void {
  gatedPersistedClient = client
}

// The provider's restore only applies a client-level age check; it has no per-query gate. Re-reading
// raw IndexedDB here would resurrect entries dropped by the pre-router gate whenever the best-effort
// prune write fails (safeSet logs and resolves quota errors), so reads use the approved in-memory
// snapshot while writes and removals still go to IndexedDB.
//
// This wiring lives here rather than in main.tsx because main.tsx is not importable under Vitest.
// A wiring mistake there is unpinnable, whereas this module is covered by tests.
export const providerPersister: Persister = {
  persistClient: client => idbPersister.persistClient(client),
  removeClient: () => idbPersister.removeClient(),
  restoreClient: async () => gatedPersistedClient,
}

/**
 * Query families never written to IndexedDB, whatever their options say.
 *
 * `meta: { persist: false }` is not enough for these: an observer's meta is
 * only attached once it mounts, and `setQueryData` can create the query
 * earlier, without it. `reportRenderJob` is seeded that way with the enqueue
 * answer, which may already carry a download token.
 */
const NEVER_PERSISTED_QUERY_FAMILIES: ReadonlySet<unknown> = new Set(['reportRenderJob'])

export const persistOptions = {
  persister: providerPersister,
  maxAge: PERSISTED_CACHE_MAX_AGE,
  // Keep the provider and the pre-router restore on one compatibility boundary.
  buster: PERSISTED_CACHE_BUSTER,
  dehydrateOptions: {
    // Persist all successful queries including infinite queries
    shouldDehydrateQuery: (query: {
      state: { status: string }
      queryKey: readonly unknown[]
      meta?: Record<string, unknown>
    }) => {
      if (NEVER_PERSISTED_QUERY_FAMILIES.has(query.queryKey[0])) return false
      if (query.meta?.persist === false) return false
      return query.state.status === 'success'
    },
  },
}
